import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/ui/PageHeader";
import { api } from "@/api/client";

/**
 * Home feed lab — the spine's "single living view" assembled server-side,
 * plus the nudge rail and global search. Sections render leniently: this
 * page exists to prove the endpoints compose, not to be the product UI.
 */

/**
 * The feed is a LIST OF SECTIONS the server composed and ordered — not a
 * fixed set of named fields. This page read the old flat shape
 * (`eventsNearYou`, `communitySnippets`) long after the server moved to
 * `sections[]`, so every block rendered its empty state against a live feed
 * and looked like a quiet database. Iterate what arrives instead: a new
 * block configured on the server (Garv, say) then shows up here without a
 * Lab change, and a renamed one cannot silently render as nothing.
 */
interface HomeSection {
  key: string;
  order: number;
  title: string;
  subtitle: string | null;
  data: unknown;
  counts?: Record<string, number>;
  dismissable: boolean;
}

interface HomeFeed {
  greeting?: Record<string, unknown> | null;
  sections?: HomeSection[];
  meta?: Record<string, unknown> | null;
}

/** One row per item, whatever the section holds. */
function sectionItems(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === "object") return [data as Record<string, unknown>];
  return [];
}

function itemLabel(item: Record<string, unknown>): string {
  for (const k of ["title", "name", "label", "content"]) {
    const v = item[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return JSON.stringify(item).slice(0, 90);
}

export default function HomeLab() {
  const { data, isLoading } = useQuery({
    queryKey: ["home-feed"],
    queryFn: () => api.get<{ success: true; data: HomeFeed }>("/home/feed"),
  });
  const feed = data?.data;

  const { data: nudgesRes } = useQuery({
    queryKey: ["nudges"],
    queryFn: () => api.get<{ success: true; data: { nudges: Array<Record<string, unknown>> } }>("/identity-board/nudges"),
  });
  const nudges = nudgesRes?.data?.nudges ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Home Feed"
        subtitle="GET /home/feed — the assembled spine: your people, your events, your community"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Home Feed" }]}
      />

      <SearchBox />

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      {nudges.length > 0 && (
        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <h2 className="font-semibold text-amber-900 mb-1 text-sm">Nudges</h2>
          {nudges.map((n, i) => (
            <p key={i} className="text-sm text-amber-800">
              {String(n.message ?? n.type ?? "")} {n.cta ? `— ${String(n.cta)}` : ""}
            </p>
          ))}
        </section>
      )}

      {feed && (feed.sections ?? []).length === 0 && (
        <Empty label="The feed returned no sections." />
      )}

      {feed &&
        (feed.sections ?? []).map((s) => {
          const items = sectionItems(s.data);
          return (
            <FeedCard key={s.key} title={s.title}>
              <p className="text-xs text-gray-400">
                {s.key} · rank {s.order}
                {s.subtitle ? ` · ${s.subtitle}` : ""}
              </p>
              {items.length === 0 ? (
                <Empty label="Rendering its zero state — the slot asked to fill when empty." />
              ) : (
                <ul className="mt-1 space-y-1">
                  {items.map((it, i) => (
                    <li key={i} className="text-sm text-gray-700">
                      {itemLabel(it)}
                      {typeof it.type === "string" && (
                        <span className="ml-2 text-xs text-gray-400">{it.type}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {s.counts && Object.keys(s.counts).length > 0 && (
                <p className="mt-1 text-xs text-gray-400">
                  {Object.entries(s.counts)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(" · ")}
                </p>
              )}
            </FeedCard>
          );
        })}
    </div>
  );
}

function SearchBox() {
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const { data, isFetching } = useQuery({
    queryKey: ["search", submitted],
    queryFn: () => api.get<{ success: true; data: Record<string, unknown> }>("/search", { q: submitted }),
    enabled: submitted.length >= 2,
  });

  const results = data?.data;

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder="Global search — providers, events, questions, people…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setSubmitted(q.trim())}
        />
        <button className="btn-primary" onClick={() => setSubmitted(q.trim())} disabled={q.trim().length < 2}>
          {isFetching ? "…" : "Search"}
        </button>
      </div>
      {results && (
        <div className="mt-3 space-y-2">
          {Object.entries(results).map(([group, items]) =>
            Array.isArray(items) && items.length > 0 ? (
              <div key={group}>
                <h3 className="text-xs font-semibold uppercase text-gray-400">{group}</h3>
                {items.slice(0, 5).map((it: Record<string, unknown>, i: number) => (
                  <p key={i} className="text-sm text-gray-700">
                    {String(it.name ?? it.title ?? it.id)}
                  </p>
                ))}
              </div>
            ) : null,
          )}
        </div>
      )}
    </section>
  );
}

function FeedCard({ title, to, children }: { title: string; to?: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold">{title}</h2>
        {to && (
          <Link to={to} className="text-sm text-primary-600 hover:underline">
            open →
          </Link>
        )}
      </div>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Empty({ label }: { label: string }) {
  return <p className="text-sm text-gray-400">{label}</p>;
}
